import React, { Component } from 'react';
import { toast } from 'react-toastify';
import { HTTPService } from '../../../services/httpService';
import { isAllDealerships } from '../../../util/locationUtils';
import '../../../assets/css/SearchResult.css';

const API = new HTTPService();

// Dealer-admin "Find an Installer" page.
// Lists installers within 50 mi of the dealership (server caps Distance < 50)
// for a selected category, and lets the admin star/unstar each one. The star is
// the `dealer_preferred` flag on installer_proximity — the same flag the
// requestimate specialist picker reads. Read endpoint requires a category
// segment, so the page is category-scoped via a dropdown sourced from the
// dealer's vendor preferences (mirrors newSpecialistForm.jsx).
export default class FindInstaller extends Component {
  _isMounted = true;

  state = {
    categories: [],          // [{ category_name, ... }] from /dealer/preferences
    selectedCategory: '',
    installerList: [],        // rows from /installerProximity/:dealerId/:category
    searchTerm: '',
    showPreferredOnly: false,
    loadingCategories: true,
    loadingInstallers: false,
    togglingKey: null         // `${Installer_id}-${Location_id}` currently in-flight
  };

  componentDidMount() {
    this.loadCategories();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // Resolve the dealer to scope proximity to: the dealership selected in the
  // CompanyLocationSelector when a group admin has drilled into one, else the
  // user's own dealership (typeId). Mirrors requestimateForm.jsx effectiveDealerId.
  get dealerId() {
    const { selectedDealerId, typeId } = this.props;
    return isAllDealerships(selectedDealerId) ? typeId : selectedDealerId;
  }

  // Vendor categories the dealer cares about — same source as the specialist
  // picker. Default to the first category and load its installers.
  loadCategories = async () => {
    try {
      const { data } = await API.get(`/dealer/preferences/${this.dealerId}`);
      const categories = Array.isArray(data) ? data : [];
      if (!this._isMounted) return;

      const first = categories[0]?.category_name || '';
      this.setState({ categories, selectedCategory: first, loadingCategories: false }, () => {
        if (first) this.loadInstallers(first);
      });
    } catch (err) {
      console.error('FindInstaller: failed to load categories', err);
      if (this._isMounted) this.setState({ categories: [], loadingCategories: false });
    }
  };

  loadInstallers = async (category) => {
    if (!category) return;
    this.setState({ loadingInstallers: true });
    try {
      const { data } = await API.get(
        `/installerProximity/${this.dealerId}/${encodeURIComponent(category)}`
      );
      if (!this._isMounted) return;
      // Server already filters Distance < 50; no client distance filtering.
      this.setState({ installerList: Array.isArray(data) ? data : [], loadingInstallers: false });
    } catch (err) {
      console.error('FindInstaller: failed to load installers', err);
      if (this._isMounted) this.setState({ installerList: [], loadingInstallers: false });
    }
  };

  handleCategoryChange = (e) => {
    const selectedCategory = e.target.value;
    this.setState({ selectedCategory, searchTerm: '' });
    this.loadInstallers(selectedCategory);
  };

  rowKey = (row) => `${row.Installer_id}-${row.Location_id}`;

  // Optimistic star toggle → PATCH dealer_preferred, reconcile from response.
  togglePreferred = async (row) => {
    const userId = this.props.user?.User_id;
    if (!userId) {
      toast.error('Unable to verify your account. Please sign in again.');
      return;
    }

    const key = this.rowKey(row);
    const isPreferred = Number(row.dealer_preferred) === 1;
    const nextPreferred = !isPreferred;

    this.setState({ togglingKey: key });
    this.setPreferredInState(key, nextPreferred ? 1 : 0); // optimistic

    try {
      const { data } = await API.patch(
        `/installer_proximity/${this.dealerId}/${row.Installer_id}/preferred`,
        { preferred: nextPreferred, locationId: row.Location_id, userId }
      );
      if (!this._isMounted) return;
      // Reconcile with server's numeric 1/0 (falls back to our optimistic value).
      const reconciled = data && data.dealer_preferred != null
        ? (Number(data.dealer_preferred) === 1 ? 1 : 0)
        : (nextPreferred ? 1 : 0);
      this.setPreferredInState(key, reconciled);
    } catch (err) {
      if (!this._isMounted) return;
      this.setPreferredInState(key, isPreferred ? 1 : 0); // revert
      const status = err?.response?.status;
      if (status === 404) {
        toast.error('This installer is not synced yet — try again shortly.');
      } else if (status === 403) {
        toast.error('You do not have permission to update this dealership.');
      } else {
        toast.error('Could not update favorite. Please try again.');
      }
      console.error('FindInstaller: toggle preferred failed', err);
    } finally {
      if (this._isMounted) this.setState({ togglingKey: null });
    }
  };

  setPreferredInState = (key, value) => {
    this.setState((prev) => ({
      installerList: prev.installerList.map((r) =>
        this.rowKey(r) === key ? { ...r, dealer_preferred: value } : r
      )
    }));
  };

  // Filter (search + preferred-only) then sort: preferred first, then by distance.
  getDisplayList = () => {
    const { installerList, searchTerm, showPreferredOnly } = this.state;
    const term = searchTerm.trim().toLowerCase();

    return installerList
      .filter((r) => {
        if (showPreferredOnly && Number(r.dealer_preferred) !== 1) return false;
        if (!term) return true;
        const haystack = [
          r.installer_name || r.Installer_Name,
          r.location_name,
          r.location_city
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(term);
      })
      .sort((a, b) => {
        const ap = Number(a.dealer_preferred) === 1 ? 0 : 1;
        const bp = Number(b.dealer_preferred) === 1 ? 0 : 1;
        if (ap !== bp) return ap - bp;
        const ad = a.Distance != null ? Number(a.Distance) : 9999;
        const bd = b.Distance != null ? Number(b.Distance) : 9999;
        return ad - bd;
      });
  };

  renderRating = (row) => {
    if (!row.google_rating) return null;
    const rating = Number(row.google_rating).toFixed(1);
    const count = row.google_review_count ? ` (${row.google_review_count})` : '';
    return <span className="text-muted ml-2">⭐ {rating}{count}</span>;
  };

  renderRow = (row) => {
    const key = this.rowKey(row);
    const isPreferred = Number(row.dealer_preferred) === 1;
    const isToggling = this.state.togglingKey === key;
    const name = row.installer_name || row.Installer_Name || 'Unnamed installer';
    const locLabel = row.location_name || row.location_city;
    const cityState = [row.location_city, row.location_state].filter(Boolean).join(', ');
    const distance = row.Distance != null ? `${Number(row.Distance).toFixed(1)} mi` : null;

    return (
      <div
        key={key}
        className="d-flex align-items-center justify-content-between border-bottom py-3 px-2"
      >
        <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
          <button
            type="button"
            className="btn btn-link p-0 mr-3"
            title={isPreferred ? 'Remove favorite' : 'Mark as favorite'}
            disabled={isToggling}
            onClick={() => this.togglePreferred(row)}
            style={{ fontSize: '1.25rem', lineHeight: 1 }}
          >
            <i
              className={`fa-sharp ${isPreferred ? 'fa-solid' : 'fa-regular'} fa-star`}
              style={{ color: isPreferred ? '#f5a623' : '#adb5bd' }}
            />
          </button>
          <div style={{ minWidth: 0 }}>
            <div className="font-weight-bold text-truncate">
              {name}
              {locLabel ? <span className="text-muted font-weight-normal"> — {locLabel}</span> : null}
              {this.renderRating(row)}
            </div>
            <div className="small text-muted text-truncate">
              {cityState}
              {row.installer_phone_number ? <span className="ml-2">{row.installer_phone_number}</span> : null}
            </div>
          </div>
        </div>
        {distance ? (
          <span className="badge badge-light border ml-3 flex-shrink-0">{distance}</span>
        ) : null}
      </div>
    );
  };

  render() {
    const {
      categories, selectedCategory, searchTerm, showPreferredOnly,
      loadingCategories, loadingInstallers
    } = this.state;
    const displayList = this.getDisplayList();

    return (
      <div className="container-fluid py-4">
        <div className="d-flex align-items-center mb-3">
          <i className="fa-sharp fa-regular fa-magnifying-glass-location mr-2" style={{ fontSize: '1.4rem' }} />
          <h4 className="mb-0">Find an Installer</h4>
        </div>
        <p className="text-muted">
          Installers within 50 miles of your dealership. Star the ones you prefer — they'll
          be promoted when you create requests.
        </p>

        {loadingCategories ? (
          <div className="text-muted py-5 text-center">Loading…</div>
        ) : categories.length === 0 ? (
          <div className="alert alert-info">
            No vendor categories are configured for your dealership yet. Set up your preferred
            categories to start finding installers.
          </div>
        ) : (
          <>
            <div className="form-row align-items-end mb-3">
              <div className="col-12 col-md-4 mb-2">
                <label className="small text-muted mb-1">Category</label>
                <select
                  className="form-control"
                  value={selectedCategory}
                  onChange={this.handleCategoryChange}
                >
                  {categories.map((c) => (
                    <option key={c.category_name} value={c.category_name}>
                      {c.category_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-5 mb-2">
                <label className="small text-muted mb-1">Search</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Filter by name or city…"
                  value={searchTerm}
                  onChange={(e) => this.setState({ searchTerm: e.target.value })}
                />
              </div>
              <div className="col-12 col-md-3 mb-2">
                <div className="custom-control custom-switch">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="preferredOnly"
                    checked={showPreferredOnly}
                    onChange={(e) => this.setState({ showPreferredOnly: e.target.checked })}
                  />
                  <label className="custom-control-label" htmlFor="preferredOnly">
                    Favorites only
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-white border rounded">
              {loadingInstallers ? (
                <div className="text-muted py-5 text-center">Loading installers…</div>
              ) : displayList.length === 0 ? (
                <div className="text-muted py-5 text-center">
                  No installers in this category within 50 miles.
                  <div className="small mt-1">
                    Newly added installers may take a short while to appear.
                  </div>
                </div>
              ) : (
                displayList.map(this.renderRow)
              )}
            </div>
          </>
        )}
      </div>
    );
  }
}
